import React, { useState } from 'react';
import Row from '@amzn/meridian/row';
import Box from '@amzn/meridian/box';
import Text from '@amzn/meridian/text';
import Icon from '@amzn/meridian/icon';
import chevronRightTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import { colorBlue50, colorBlue600, colorGray50 } from '@amzn/meridian-tokens/base/color';

/**
 * In-page section navigation for the internal areas (Supplier Control and
 * Supplier Insights). A borderless, lightweight pill list: each item is a
 * rounded row that fills with the accent tint when selected (bold text + a
 * trailing chevron). No surrounding box, so it reads as navigation rather than
 * a heavy container. Shared so both internal screens look uniform.
 */
export interface SectionNavItem<T extends string> {
    id: T;
    label: string;
}

/** Fixed rail width in pixels. */
const RAIL_WIDTH = 240;

interface SectionNavProps<T extends string> {
    items: SectionNavItem<T>[];
    active: T;
    onSelect: (id: T) => void;
}

function NavItem({
    label,
    selected,
    onClick,
}: {
    label: string;
    selected: boolean;
    onClick: () => void;
}) {
    const [hover, setHover] = useState(false);

    let background = 'transparent';
    if (selected) background = colorBlue50;
    else if (hover) background = colorGray50;

    return (
        <button
            type="button"
            onClick={onClick}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            aria-current={selected ? 'page' : undefined}
            style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                border: 'none',
                borderRadius: 10,
                background,
                cursor: 'pointer',
                padding: 0,
                transition: 'background 120ms ease',
            }}
        >
            <Box spacingInset="300 400">
                <Row alignmentHorizontal="justify" alignmentVertical="center" spacing="300">
                    <Text
                        type={selected ? 'h100' : 'b300'}
                        color={selected ? undefined : 'secondary'}
                    >
                        {label}
                    </Text>
                    {selected ? (
                        <div style={{ color: colorBlue600, display: 'flex' }}>
                            <Icon tokens={chevronRightTokens}>Selected</Icon>
                        </div>
                    ) : null}
                </Row>
            </Box>
        </button>
    );
}

export default function SectionNav<T extends string>({
    items,
    active,
    onSelect,
}: SectionNavProps<T>) {
    return (
        <nav
            aria-label="Section navigation"
            style={{ width: RAIL_WIDTH, minWidth: RAIL_WIDTH, flexShrink: 0 }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, position: 'sticky', top: 16 }}>
                {items.map((item) => (
                    <NavItem
                        key={item.id}
                        label={item.label}
                        selected={item.id === active}
                        onClick={() => onSelect(item.id)}
                    />
                ))}
            </div>
        </nav>
    );
}
